import { ReactNode } from 'react'
import styled from 'styled-components'

import {
  Color,
  HStack,
  Icon,
  IconButton,
  IconName,
  Text,
  Token,
} from '@revolut-internal/ui-kit'

import { Tooltip } from 'components/Tooltip/Tooltip'
import { SpecialisationData } from 'types'

import { QueueHealthDetailFormattedData } from '../types'

import { ShowResourcesCell, ShowResourcesCellProps } from './ShowResourcesCell'

type IncidentCellProps = ShowResourcesCellProps & {
  label?: ReactNode
  onCopyReport: (item: QueueHealthDetailFormattedData) => void
}

const StyledButton = styled(IconButton)`
  transition: ${Token.transition.opacity};

  &:not([data-visible='true']) {
    opacity: 0;
    pointer-events: none;
  }
`
const StyledCell = styled(ShowResourcesCell)`
  &:hover {
    ${StyledButton} {
      &:not([data-visible='true']) {
        opacity: 1;
        pointer-events: auto;
      }
    }
  }
`
type IncidentStatus = 'noIncidents' | 'noPolicy' | 'acknowledged' | 'ongoing'

const ICON_ALWAYS_VISIBLE_STATUSES = ['acknowledged', 'ongoing']

const getIncidentConfig = (
  specialisation?: SpecialisationData,
): {
  incidentStatus: IncidentStatus
  incidentIcon: IconName
  incidentColor: Color
  incidentDescription: string
} => {
  const incidents = specialisation?.ongoingIncidents

  if (!specialisation?.alertingPolicies?.length) {
    return {
      incidentStatus: 'noPolicy',
      incidentIcon: 'ExclamationMarkOutline',
      incidentColor: Token.color.greyTone50,
      incidentDescription: 'No policy set',
    }
  }

  if (!incidents?.length) {
    return {
      incidentStatus: 'noIncidents',
      incidentIcon: 'ExclamationMarkOutline',
      incidentColor: Token.color.greyTone50,
      incidentDescription: 'No ongoing incidents',
    }
  }

  const isAcknowledged = incidents.every(incident => incident.isAcknowledged)

  if (isAcknowledged) {
    return {
      incidentStatus: 'acknowledged',
      incidentIcon: 'ExclamationMarkOutline',
      incidentColor: Token.color.warning,
      incidentDescription: 'Incident acknowledged',
    }
  }

  return {
    incidentStatus: 'ongoing',
    incidentIcon: 'ExclamationMark',
    incidentColor: Token.color.terracotta,
    incidentDescription: 'Incident ongoing',
  }
}

export const IncidentCell = ({
  label,
  onCopyReport,
  ...showResourcesProps
}: IncidentCellProps) => {
  const row = showResourcesProps.cell.row

  const { incidentStatus, incidentIcon, incidentColor, incidentDescription } =
    getIncidentConfig(row.original)

  return (
    <StyledCell aria-label={row.original.name} {...showResourcesProps}>
      <HStack space="s-12" justifyContent="space-between" flexWrap="nowrap">
        <Text variant="emphasis2" overflow="hidden" textOverflow="ellipsis">
          {label ?? row.original.name ?? ''}
        </Text>

        <HStack space="s-12" align="center" style={{ flexShrink: 0 }}>
          <Tooltip message="Copy report">
            <StyledButton
              useIcon="Document"
              size={16}
              color="grey-tone-50"
              useHoverIcon={<Icon name="Document" size={16} color="accent" />}
              iconOnly
              onClick={e => {
                e.stopPropagation()
                onCopyReport(row.original)
              }}
            >
              Copy report
            </StyledButton>
          </Tooltip>

          <Tooltip message={incidentDescription}>
            <StyledButton
              useIcon={incidentIcon}
              size={16}
              color={incidentColor}
              useHoverIcon={<Icon name={incidentIcon} size={16} color={incidentColor} />}
              data-visible={ICON_ALWAYS_VISIBLE_STATUSES.includes(incidentStatus)}
              iconOnly
            >
              {incidentDescription}
            </StyledButton>
          </Tooltip>
        </HStack>
      </HStack>
    </StyledCell>
  )
}
